import dayjs from "dayjs";

export const barOptions = (barAnnotation, showValues) => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 60,
      },
    },
    plugins: {
      title: {
        display: false,
        text: "Monthly Payer Summary Report",
      },
      tooltip: {
        enabled: !showValues,
      },
      annotation: {
        annotations: barAnnotation,
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: showValues, // Toggle display of values
        anchor: "end",
        align: "top",
        formatter: (value) => `$${value.toLocaleString()}`,
        font: {
          weight: "bold",
          size: 12,
        },
        color: "#000",
        rotation: 90,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90,
          font: {
            size: 10,
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          display: true,
          callback: function (value) {
            return "$" + value.toLocaleString();
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };
};

export const processRawData = async (data, setBarAnnotation, theme) => {
  const topPayersLimit = 10; // Number of top payers to display
  const rawData = {};

  data.forEach((item) => {
    const { year_month_received, payer_name, payment_received } = item;

    // Initialize the data structure if not exists
    if (!rawData[year_month_received]) {
      rawData[year_month_received] = {
        total: 0,
      };
    }

    // Add the payment received to the total for the specific year_month_received
    rawData[year_month_received].total += payment_received;

    // If the payer is "Other", group it under "Other"
    const payerKey =
      payer_name === "Other" ? "Other" : payer_name.toUpperCase();
    if (!rawData[year_month_received][payerKey]) {
      rawData[year_month_received][payerKey] = 0;
    }

    // Add the payment received to the payer's total
    rawData[year_month_received][payerKey] += payment_received;
  });

  // Now, transform rawData to the desired format
  const labels = [];
  const dataValues = [];
  const groupPositions = [];
  let currentIndex = 0;

  // Process the rawData into the format expected for the chart
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Object.entries(rawData).forEach(([_, companies]) => {
    groupPositions.push(currentIndex); // Save the starting index for each date group
    const sortedCompanies = Object.entries(companies)
      .filter(([company]) => company !== "total")
      .sort((a, b) => b[1] - a[1]); // Sort by value (amount)

    // Get the top N payers and group the rest as "Other Payers"
    const topPayers = sortedCompanies.slice(0, topPayersLimit);
    const otherPayers = sortedCompanies.slice(topPayersLimit);
    if (otherPayers.length > 0) {
      topPayers.push([
        "Other Payers",
        otherPayers.reduce((sum, payer) => sum + payer[1], 0),
      ]);
    }

    topPayers.forEach(([company, value]) => {
      labels.push(` ${company}`);
      dataValues.push(value);
      currentIndex++;
    });
  });

  // Set the annotations after groupPositions is calculated
  const annotation = groupPositions.map((pos, index) => ({
    type: "line",
    mode: "vertical",
    scaleID: "x",
    value: pos - 0.5,
    borderColor: "black",
    borderWidth: 1,
    label: {
      content: Object.keys(rawData)[index], // This now uses rawData directly
      enabled: true,
      font: {
        size: 12,
        weight: "bold",
      },
      backgroundColor: "#0000",
      display: true,
      color: "black",
      position: "start",
      xAdjust: 30,
      yAdjust: -10,
    },
  }));
  setBarAnnotation(annotation);

  return {
    labels,
    datasets: [
      {
        label: "Amount ($)",
        data: dataValues,
        backgroundColor: theme.palette.primary.main,
        barPercentage: 0.6,
        categoryPercentage: 0.9,
      },
    ],
  };
};

export const transformDataForGrid = (data) => {
  // Extract unique months and sort them
  const uniqueMonths = [
    ...new Set(data.map((item) => item.year_month_received)),
  ].sort();

  // Extract unique payers
  const uniquePayers = [...new Set(data.map((item) => item.payer_name))];

  // Initialize rows for DataGrid
  const rows = uniquePayers.map((payer, index) => {
    let row = { id: index + 1, payer: payer };

    // Populate row with payment data for each month
    uniqueMonths.forEach((month) => {
      const record = data.find(
        (item) =>
          item.payer_name === payer && item.year_month_received === month
      );
      row[month] = record ? `$${record.payment_received}` : "-"; // Show empty if no payment
    });

    return row;
  });

  // Define columns dynamically
  const columns = [
    { field: "payer", headerName: "Payer", width: 250 },
    ...uniqueMonths.map((month) => ({
      field: month,
      headerName: dayjs(month).format("MMM-YYYY"),
      width: 120,
      align: "center",
    })),
  ];

  return { columns, rows };
};
